import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Alert,
  CircularProgress
} from '@mui/material';
import { assessmentsApi } from '../services/api';

/**
 * Trending Report Dialog
 * 
 * Allows selecting two assessments of the same type to compare
 */
const TrendingReportDialog = ({ 
  open, 
  onClose, 
  clientId,
  clientName,
  onCompare 
}) => {
  const [assessmentType, setAssessmentType] = useState('');
  const [availableAssessments, setAvailableAssessments] = useState([]);
  const [baselineId, setBaselineId] = useState('');
  const [currentId, setCurrentId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [assessmentTypes, setAssessmentTypes] = useState([]);

  useEffect(() => {
    if (open && clientId) {
      loadAssessments();
    }
  }, [open, clientId]);

  const loadAssessments = async () => {
    try {
      setLoading(true);
      
      // Get all assessments for this client
      const response = await assessmentsApi.getAll({ client_id: clientId });
      const assessments = response.data.assessments || [];
      
      // Group by assessment type
      const typeGroups = {};
      assessments.forEach(assessment => {
        if (assessment.status === 'completed') { // Only show completed assessments
          const typeId = assessment.assessment_type_id;
          if (!typeGroups[typeId]) {
            typeGroups[typeId] = {
              id: typeId,
              name: assessment.assessment_type_name,
              assessments: []
            };
          }
          typeGroups[typeId].assessments.push(assessment);
        }
      });
      
      // Convert to array and filter types with at least 2 assessments
      const types = Object.values(typeGroups).filter(type => type.assessments.length >= 2);
      setAssessmentTypes(types);
      
      if (types.length === 0) {
        setError('No assessment types with at least 2 completed assessments found');
      }
      
    } catch (error) {
      console.error('Error loading assessments:', error);
      setError('Failed to load assessments');
    } finally {
      setLoading(false);
    }
  };

  const handleTypeChange = (e) => {
    const typeId = e.target.value;
    setAssessmentType(typeId);
    setBaselineId('');
    setCurrentId('');
    
    // Find assessments for this type
    const selectedType = assessmentTypes.find(t => t.id === typeId);
    if (selectedType) {
      // Sort by created_at (oldest first)
      const sorted = [...selectedType.assessments].sort((a, b) => 
        new Date(a.created_at) - new Date(b.created_at)
      );
      setAvailableAssessments(sorted);
    }
  };

  const handleCompare = async () => {
    // Validation
    if (!assessmentType) {
      setError('Please select an assessment type');
      return;
    }
    
    if (!baselineId || !currentId) {
      setError('Please select both baseline and current assessments');
      return;
    }
    
    if (baselineId === currentId) {
      setError('Please select two different assessments');
      return;
    }
    
    // Verify baseline is older than current
    const baseline = availableAssessments.find(a => a.id === parseInt(baselineId));
    const current = availableAssessments.find(a => a.id === parseInt(currentId));
    
    if (new Date(baseline.created_at) > new Date(current.created_at)) {
      setError('Baseline assessment must be older than current assessment');
      return;
    }
    
    setError('');
    
    try {
      await onCompare(parseInt(baselineId), parseInt(currentId));
      onClose();
    } catch (error) {
      console.error('Error comparing assessments:', error);
      setError(error.response?.data?.error || error.message || 'Failed to compare assessments');
    }
  };

  const handleClose = () => {
    setAssessmentType('');
    setBaselineId('');
    setCurrentId('');
    setAvailableAssessments([]);
    setError('');
    onClose();
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        View Trending Report
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Client: <strong>{clientName}</strong>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Compare two completed assessments to see progress over time
          </Typography>
        </Box>

        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress />
          </Box>
        )}

        {!loading && assessmentTypes.length === 0 && (
          <Alert severity="warning">
            No assessment types with at least 2 completed assessments found. You need at least 2 completed assessments of the same type to generate a trending report.
          </Alert>
        )}

        {!loading && assessmentTypes.length > 0 && (
          <>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <FormControl fullWidth sx={{ mb: 3 }}>
              <InputLabel>Assessment Type</InputLabel>
              <Select
                value={assessmentType}
                onChange={handleTypeChange}
                label="Assessment Type"
              >
                {assessmentTypes.map(type => (
                  <MenuItem key={type.id} value={type.id}>
                    {type.name} ({type.assessments.length} completed)
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {assessmentType && availableAssessments.length > 0 && (
              <>
                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Baseline Assessment (Older)</InputLabel>
                  <Select
                    value={baselineId}
                    onChange={(e) => setBaselineId(e.target.value)}
                    label="Baseline Assessment (Older)"
                  >
                    {availableAssessments.map(assessment => (
                      <MenuItem key={assessment.id} value={assessment.id}>
                        {assessment.name} - {formatDate(assessment.created_at)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <FormControl fullWidth sx={{ mb: 2 }}>
                  <InputLabel>Current Assessment (Newer)</InputLabel>
                  <Select
                    value={currentId}
                    onChange={(e) => setCurrentId(e.target.value)}
                    label="Current Assessment (Newer)"
                  >
                    {availableAssessments.map(assessment => (
                      <MenuItem key={assessment.id} value={assessment.id}>
                        {assessment.name} - {formatDate(assessment.created_at)}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Alert severity="info" sx={{ mt: 2 }}>
                  <Typography variant="body2">
                    <strong>Tip:</strong> Select an older assessment as the baseline and a newer one as current to see progress over time.
                  </Typography>
                </Alert>
              </>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose}>
          Cancel
        </Button>
        <Button 
          onClick={handleCompare}
          variant="contained"
          disabled={!baselineId || !currentId || baselineId === currentId}
          sx={{ 
            backgroundColor: '#3498db',
            '&:hover': { backgroundColor: '#2980b9' }
          }}
        >
          View Trending Report
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TrendingReportDialog;
